import { Box, Button, TextField, Typography } from "@mui/material";
import { FC, useState } from "react";
import { LoadImgForAds } from "./LoadImgForAds";
import { usePostBodyWithJwtMutation } from "src/redux/api/cabinet";
import { Alerting } from "src/views/components_cabinet/Alerting";
import { ReturnNumber } from "src/components/return-balance/ReturnNumber";
import { palette } from "src/theme/style/palette";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface ISetPurchase {
    active: any;
    reload?: () => void;
}

export const SetPurchase: FC<ISetPurchase> = ({ active: item }) => {
    const [wantAmount, setWantAmount] = useState<string>("");
    const [getAlert, setGetAlert] = useState<{ type: number; msg: string } | undefined>(undefined);

    const [postData, { isLoading, isSuccess }] = usePostBodyWithJwtMutation();

    const registerAnswer = async (body: any) => {
        const route = "p2p-bron-add";
        const jwt = localStorage.getItem("jwt");
        await postData({ body, route, jwt })
            .unwrap()
            .catch((error: any) => {
                setGetAlert({ type: 2, msg: error.data.message });
            });
    };

    const isAmountValid = (amount: string) => {
        const numAmount = parseFloat(amount);
        return amount !== "" && numAmount > 0 && numAmount <= (item?.amount || 0);
    };

    // Получение текста ошибки
    const getErrorText = () => {
        const numAmount = parseFloat(wantAmount);
        if (wantAmount === "" || numAmount <= 0) {
            return "Сумма должна быть больше нуля";
        }
        if (numAmount > (item?.amount || 0)) {
            return `Сумма не может превышать ${item?.amount}`;
        }
        return "";
    };

    // Расчет суммы к оплате в валюте
    const calculatePaymentAmount = () => {
        const numAmount = parseFloat(wantAmount);
        const exchangeRate = item?.course || 1;
        if (!isNaN(numAmount) && numAmount > 0) {
            return (numAmount * exchangeRate).toFixed(2);
        }
        return "0.00";
    };

    return (
        <>
            {getAlert && <Alerting get={getAlert} />}

            <Box width="100%">
                {isSuccess ? (
                    <LoadImgForAds id={item} />
                ) : (
                    <Box mt={3}>
                        <Typography textAlign='center' variant="h5">Хотите забронировать объявление №{item.id}?</Typography>
                        <Typography textAlign='center' variant="h5" sx={{ fontWeight: 600, my: 3 }}><ReturnNumber num={item.amount} /> USDT</Typography>

                        <Box p={2} sx={{ border: `1px solid ${palette.blue}`, mb: 2 }}>
                            <Typography textAlign='center' sx={{ fontWeight: 600 }}>
                                Курс обмена: <Box component="span" sx={{ fontWeight: 700, color: palette.success }}>
                                    1 USDT = {item?.course || 1} ({item?.currency_id})
                                </Box>
                            </Typography>
                        </Box>

                        <Box p={2} sx={{ border: `1px solid ${palette.blue}` }}>
                            <Typography textAlign='center' sx={{ fontWeight: 600 }}>
                                Условия продажи: <Box component="span" sx={{ fontWeight: 600 }}>{item?.account}</Box>
                            </Typography>
                        </Box>

                        <Box
                            mt={2}
                            sx={{
                                backgroundColor: '#fdf5e3ff',
                                border: '1px solid #d29e19ff',
                                borderRadius: 1,
                                padding: 1.5,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1
                            }}
                        >
                            <InfoOutlinedIcon sx={{ color: '#d29e19ff', fontSize: 24, mt: 0.2 }} />
                            <Typography variant="body2" sx={{ fontSize: 18, color: '#906c12ff', lineHeight: 1.5 }}>
                                После бронирования у вас будет 1 час на оплату, после чего объявление будет снято с брони
                            </Typography>
                        </Box>

                        <Box gap={2} mt={5} display="flex" justifyContent="center" flexDirection='column' alignItems='center'>
                            <TextField
                                label="Введите сумму USDT"
                                type="text"
                                value={wantAmount}
                                fullWidth
                                inputProps={{
                                    inputMode: 'decimal',
                                }}
                                onInput={(e) => {
                                    const target = e.target as HTMLInputElement;
                                    let value = target.value;

                                    // Разрешаем только цифры и одну точку
                                    value = value.replace(/[^\d.]/g, '');

                                    // Разрешаем только одну точку
                                    const parts = value.split('.');
                                    if (parts.length > 2) {
                                        value = parts[0] + '.' + parts.slice(1).join('');
                                    }

                                    // Ограничиваем до 4 знаков после точки
                                    if (parts.length === 2 && parts[1].length > 4) {
                                        value = parts[0] + '.' + parts[1].slice(0, 4);
                                    }

                                    target.value = value;
                                    setWantAmount(value);
                                }}
                                error={wantAmount !== "" && !isAmountValid(wantAmount)}
                                helperText={wantAmount !== "" && !isAmountValid(wantAmount) ? getErrorText() : `Максимальная сумма: ${item?.amount} USDT`}
                                placeholder="0.00"
                            />

                            {wantAmount !== "" && !isNaN(parseFloat(wantAmount)) && parseFloat(wantAmount) > 0 && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        p: 2,
                                        backgroundColor: '#e8f5e9',
                                        border: `2px solid ${palette.success}`,
                                        borderRadius: 2,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: '#2e7d32', mb: 1 }}>
                                        Сумма к оплате:
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: palette.success }}>
                                        {calculatePaymentAmount()} {item?.currency}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#2e7d32', mt: 1, display: 'block' }}>
                                        ({wantAmount} USDT × {item?.course || 1} курс)
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                fullWidth
                                disabled={isLoading || !isAmountValid(wantAmount)}
                                onClick={() => registerAnswer({ exchange: item.id, amount: parseFloat(wantAmount) })}
                            >
                                Забронировать
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    );
};